import { compose, apply, map, prop, reduce, add, ifElse, isEmpty, always, has, filter, sum, isNil, path, find, propEq, flatten, includes, propOr, is } from 'ramda';
import { widgetHeight } from './widget';

const HEIGHT_MAX_A4_WITHOUT_MARGINS = 1123; // max height A4

/**
 * It takes a list of pages, and returns a list of heights for each page
 * @param widgetsHeader - header widgets
 * @param pagesOnSection - an array of pages, each page is an array of widgets
 * @param widgetsFooter - footer widgets
 * @param widgetsList - the list of all widgets in the section
 * @param padding - {top: number, bottom: number}
 */
const getPagesHeight = (widgetsHeader, pagesOnSection, widgetsFooter, widgetsList, padding) => {
  const getWidgetHeight = (rowWidgets) => compose(
    apply(Math.max),
    map((widget) => {
      if (rowWidgets.length > 1) {
        if (widget === rowWidgets[0]) {
          return compose(
            apply(Math.max),
            map((w) => widgetHeight(widgetsList, prop('widgetId', w))(w))
          )(rowWidgets);
        }
      }
      return widgetHeight(widgetsList, prop('widgetId', widget))(widget);
    })
  )(rowWidgets);

  const contentHeight = (widgetsPage) => compose(
    reduce(add, 0),
    map(
      compose(
        ifElse(
          isEmpty,
          always(0),
          getWidgetHeight
        ),
        filter(has('widgetId'))
      )
    ),
    map(prop('widgets'))
  )(widgetsPage);

  return map(
    compose(
      add(prop('top', padding)),
      add(prop('bottom', padding)),
      add(contentHeight(widgetsFooter)),
      add(contentHeight(widgetsHeader)),
      contentHeight
    )
  )(pagesOnSection);
};

const getPageWidth = (widgetsPage, widgetsList, padding) => compose(
  add(prop('right', padding)),
  add(prop('left', padding)),
  apply(Math.max),
  map(compose(
    sum,
    map(compose(
      ifElse(
        isNil,
        always(0),
        compose(
          apply(Math.max),
          map((element) => (path(['size', 'width'], element))),
          JSON.parse,
          path(['contents'])
        )
      ),
      (id) => find(propEq(id, 'id'))(widgetsList)
    )),
    map(path(['widgetId'])),
    filter(has('widgetId'))
  )),
  map(path(['widgets']))
)(widgetsPage);

/**
 * Splits an array of HTML strings into pages according to CSS page breaks (break-after:page)
 * @param {string[]} htmlArray - An array of HTML strings
 * @returns {string[][]} - An array of pages, each page being an array of HTML strings
 */
const splitPagesByBreak = (htmlArray) => {
  const parser = new DOMParser();
  return compose(
    flatten,
    map(map((div) => prop('outerHTML', div))),
    map(filter((div) => {
      const style = div.getAttribute('style');
      return style && includes('break-after:page', style);
    })),
    map((doc) => Array.from(path(['body', 'children'], doc))),
    map((html) => parser.parseFromString(html, 'text/html'))
  )(htmlArray);
};

/**
 * Traverses a nested structure and applies a verification function to the rules.
 *
 * @param {Array} items - The list of elements to traverse.
 * @param {Function} checkFn - The verification function to apply to each rule.
 * @returns {Array} - The matching rules after applying the verification function.
 */
const traverseAndCheckRules = (items, checkFn) => compose(
  flatten,
  map(item => {
    if (is(Array, item)) return traverseAndCheckRules(item, checkFn);

    const widgets = propOr([], 'widgets', item);
    const header = propOr([], 'header', item);
    const footer = propOr([], 'footer', item);
    const rules = [...propOr([], 'rules', item), ...propOr([], 'rulesBreakPage', item)];

    const matchedRules = filter(checkFn, rules);

    return [
      ...matchedRules,
      ...traverseAndCheckRules(widgets, checkFn),
      ...traverseAndCheckRules(header, checkFn),
      ...traverseAndCheckRules(footer, checkFn)
    ];
  })
)(items);

export {
  HEIGHT_MAX_A4_WITHOUT_MARGINS,
  getPagesHeight,
  getPageWidth,
  splitPagesByBreak,
  traverseAndCheckRules
};
